/**
 * OPEN-109 — platform support settings (migration 0160).
 *
 * One row, owned by the PLATFORM tenant (same RLS and owner guard as
 * platform_billing_settings): the support address shown to users, and where mail RECEIVED at
 * it is forwarded. Both change in the admin console with no code change or deploy.
 * `platform_support_contact()` (SECURITY DEFINER) is the read path that needs no platform-
 * tenant context: the sign-in help line and email footers use it.
 */
import { check, pgPolicy, pgTable, text, timestamp, uuid } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { appUserRole } from './enums.js';
import { tenants, users } from './users.js';

/** Shown to users when no support address has been saved. */
export const DEFAULT_SUPPORT_EMAIL = 'support@lodexpro.com';

const ownerIsolation = sql`owner_tenant_id = current_setting('app.current_tenant_id', true)::uuid`;

export const platformSupportSettings = pgTable(
  'platform_support_settings',
  {
    ownerTenantId: uuid('owner_tenant_id')
      .primaryKey()
      .references(() => tenants.id),
    /** NULL = DEFAULT_SUPPORT_EMAIL. */
    supportEmail: text('support_email'),
    /** NULL = keep received mail in S3 and log a warning. */
    forwardToEmail: text('forward_to_email'),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
    updatedBy: uuid('updated_by').references(() => users.id, { onDelete: 'set null' }),
  },
  (t) => [
    check(
      'platform_support_settings_support_email_shape',
      sql`${t.supportEmail} IS NULL OR ${t.supportEmail} ~ '^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$'`,
    ),
    check(
      'platform_support_settings_forward_to_shape',
      sql`${t.forwardToEmail} IS NULL OR ${t.forwardToEmail} ~ '^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$'`,
    ),
    check(
      'platform_support_settings_lowercase',
      sql`(${t.supportEmail} IS NULL OR ${t.supportEmail} = lower(${t.supportEmail})) AND (${t.forwardToEmail} IS NULL OR ${t.forwardToEmail} = lower(${t.forwardToEmail}))`,
    ),
    pgPolicy('platform_support_settings_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: ownerIsolation,
      withCheck: ownerIsolation,
    }),
  ],
);

export type PlatformSupportSettingsRow = typeof platformSupportSettings.$inferSelect;
